import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import { setRememberEmailEnabled, setRememberedEmail, clearRememberedEmail, clearInflightEmail } from '../../../utils/rememberEmail';
import { roleChangeError } from '../../../utils/roleChangeError';
import ModulesSection from './ModulesSection';
import TimeZonePicker from './TimeZonePicker';
import SecretInput from './SecretInput';
import { CAPABILITY_GROUPS, permissionSections, matrixActions } from './permissionCapabilities';
import { useCapabilities, useAllowedTabs } from '../../../utils/capabilities';
import ScenariosSection from './Scenarios/ScenariosSection';
import { useSettingsRequest } from './useSettingsRequest';
import './Settings.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const getAuthHeaders = () => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const activeOrgId = typeof localStorage !== 'undefined' ? localStorage.getItem('active_org_id') : null;
  if (activeOrgId) headers['X-Active-Org-Id'] = activeOrgId;
  return headers;
};

const ORGANIZATION_NAMES = [
  'Canadian Armed Forces',
  'Emergency Health Services',
  'Royal Canadian Navy',
  'Nova Scotia Health Authority',
  'Canadian Red Cross',
  'Provincial Health Authority',
];


const DATA_REGIONS = [
  'Canada',
  'United States',
  'European Union',
  'Asia Pacific',
  'Other',
];

// Capabilities: id maps to backend action name in role.permissions.actions
const PERMISSION_CAPABILITIES = [
  { id: 'manage_users', label: 'Manage Users', action: 'manage_users' },
  { id: 'permissions_management', label: 'Permissions Management', action: 'configure_sso' },
  { id: 'add_datasets', label: 'Add Datasets', action: 'create_edit_dataset' },
  { id: 'record_audio_dataset', label: 'Record Audio Dataset', action: 'record_audio_dataset' },
  { id: 'delete_datasets', label: 'Delete Datasets', action: 'delete_dataset' },
  { id: 'create_connections', label: 'Create Connections', action: 'create_edit_connections' },
  { id: 'create_automations', label: 'Create Automations', action: 'create_edit_automations' },
  { id: 'use_ai', label: 'Use AI / Ask ResAI', action: 'use_ai' },
  { id: 'saved_queries', label: 'Saved Queries', action: 'save_queries_views' },
  { id: 'view_audit_logs', label: 'View Audit Logs', action: 'view_audit_logs' },
];

function validatePermissionCapabilities(capabilities) {
  if (!Array.isArray(capabilities)) {
    console.error('PERMISSION_CAPABILITIES must be an array.');
    return;
  }
  const seenIds = new Set();
  capabilities.forEach((cap, index) => {
    if (!cap || typeof cap !== 'object') {
      console.error(`PERMISSION_CAPABILITIES[${index}] is not a valid object:`, cap);
      return;
    }
    const { id, label, action } = cap;
    if (typeof id !== 'string' || id.trim() === '') {
      console.error(`PERMISSION_CAPABILITIES[${index}] has an invalid "id":`, id);
    }
    if (typeof label !== 'string' || label.trim() === '') {
      console.error(`PERMISSION_CAPABILITIES[${index}] has an invalid "label":`, label);
    }
    if (typeof action !== 'string' || action.trim() === '') {
      console.error(`PERMISSION_CAPABILITIES[${index}] has an invalid "action":`, action);
    }
    if (seenIds.has(id)) {
      console.error(`Duplicate capability "id" in PERMISSION_CAPABILITIES: "${id}".`);
    } else {
      seenIds.add(id);
    }
  });
}
validatePermissionCapabilities(PERMISSION_CAPABILITIES);
Object.freeze(PERMISSION_CAPABILITIES);
PERMISSION_CAPABILITIES.forEach((cap) => Object.freeze(cap));

CAPABILITY_GROUPS.forEach((g) => validatePermissionCapabilities(g.capabilities));
const MATRIX_ACTIONS = matrixActions(PERMISSION_CAPABILITIES);

// Display name for role in dropdown (API role_name -> UI label)
const ROLE_DISPLAY_NAMES = {
  Owner: 'Platform Owner',
  Contributor: 'Contributor',
  'Data Steward': 'Data Steward',
  'Automation Operator': 'Automation Operator',
  Analyst: 'Analyst',
  Reader: 'Reader',
  Auditor: 'Auditor',
  'Super Admin': 'Super Admin',
};

const roleDisplayName = (roleName) => ROLE_DISPLAY_NAMES[roleName] || roleName;

function Settings({ onShowNotification, onShowToast, me: appMe, onRefreshMe, onLogout, libraryModel, assessmentsModel = null, requestedTab = null, addScenarioOnOpen = false, onReturnToAddAssessment, onRequestHandled }) {
  const moduleFrameworkEnabled = useFlag('enable_module_framework');
  // PRD-1699: with `role-capabilities` on, only the tabs the role allows show, and the
  // page opens on the first of them. SSO setup lives on Organization, so either
  // capability opens that tab. PRD-1701: Assessments shows while `navy-assessments` is
  // on (App passes the model then); if it turns off while open, the page moves to the
  // first tab shown rather than leave an empty page.
  // With none of them nothing shows.
  const caps = useCapabilities(appMe?.features);
  const navyPermissionsEnabled = useFlag('navy-assessment-permissions');
  const { shown: settingsTabs, active: activeTab, setActive: setActiveTab } = useAllowedTabs(caps, [
    { id: 'organization', label: 'Organization', allowed: caps.has('editOrganization') || caps.has('manageSso') },
    { id: 'users', label: 'Users', allowed: caps.has('manageUsers') },
    { id: 'permissions', label: 'Permissions Management', allowed: caps.has('managePermissions') },
    ...(libraryModel ? [{ id: 'assessments', label: 'Assessments', allowed: caps.has('assessmentSetup') }] : []),
  ]);
  // PRD-1703: the way here from Add assessment, and back.
  const { returnToAdd, backToAddAssessment } = useSettingsRequest({
    tabs: settingsTabs, setActiveTab, requestedTab, addScenarioOnOpen, onRequestHandled, assessmentsModel, onReturnToAddAssessment,
  });
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [rolesWithPermissions, setRolesWithPermissions] = useState([]);
  const [permissionMatrix, setPermissionMatrix] = useState({});
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [pendingRoleByUserId, setPendingRoleByUserId] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const fetchMe = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/api/v1/user-profile`, { headers: getAuthHeaders() });
    if (res.ok) return res.json();
    return null;
  }, []);

  const fetchUsers = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/api/v1/users`, { headers: getAuthHeaders() });
    if (res.ok) return res.json();
    return [];
  }, []);

  const fetchRoles = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/api/v1/roles`, { headers: getAuthHeaders() });
    if (res.ok) return res.json();
    return [];
  }, []);

  const fetchRolesWithPermissions = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/api/v1/roles/with-permissions`, { headers: getAuthHeaders() });
    if (res.ok) return res.json();
    return [];
  }, []);

  // PRD-1699: the org's users and roles are fetched only for a role shown the Users
  // tab, not fetched and hidden.
  const usersTabShown = settingsTabs.some((tab) => tab.id === 'users');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [meData, usersData, rolesData] = await Promise.all([
        fetchMe(),
        usersTabShown ? fetchUsers() : [],
        usersTabShown ? fetchRoles() : [],
      ]);
      if (!cancelled) {
        setMe(meData);
        setUsers(usersData);
        setRoles(rolesData);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchMe, fetchUsers, fetchRoles, usersTabShown]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers().then(setUsers);
      if (roles.length === 0) fetchRoles().then(setRoles);
    }
  }, [activeTab, fetchUsers, fetchRoles, roles.length]);

  // Close action menu when clicking outside
  useEffect(() => {
    if (!openMenuUserId) return;
    const handleClick = (e) => {
      if (!e.target.closest('.settings-actions-wrapper')) {
        setOpenMenuUserId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuUserId]);

  useEffect(() => {
    if (activeTab === 'permissions') {
      fetchRolesWithPermissions().then((list) => {
        setRolesWithPermissions(list);
        const matrix = {};
        MATRIX_ACTIONS.forEach((action) => {
          matrix[action] = {};
          list.forEach((r) => {
            const actions = (r.permissions && r.permissions.actions) || [];
            matrix[action][r.id] = actions.includes(action);
          });
        });
        setPermissionMatrix(matrix);
      });
    }
  }, [activeTab, fetchRolesWithPermissions]);

  // PRD-1695: a toggle grants or revokes only its own action, so actions this
  // page doesn't show stay on the role. A refused save puts the toggle back.
  // The matrix is keyed by action, so two rows for one action never disagree.
  const permissionRows = permissionSections(PERMISSION_CAPABILITIES, { capabilitiesOn: caps.enabled, navyOn: navyPermissionsEnabled });
  const togglePermission = (role, cap, newOn) => {
    // Only a row the flags show can change (CLAUDE.md: gate handlers, not just JSX).
    if (!permissionRows.some((section) => section.capabilities.includes(cap))) return;
    const setCell = (value) => setPermissionMatrix((m) => ({
      ...m,
      [cap.action]: { ...(m[cap.action] || {}), [role.id]: value },
    }));
    setCell(newOn);
    setPermissionSaving(true);
    apiService.setRoleAction(role.id, cap.action, newOn)
      .then(() => {
        setRolesWithPermissions((list) => list.map((r) => {
          if (r.id !== role.id) return r;
          const held = (r.permissions?.actions || []).filter((a) => a !== cap.action);
          return { ...r, permissions: { ...r.permissions, actions: newOn ? [...held, cap.action] : held } };
        }));
        onShowNotification && onShowNotification('Permissions updated');
      })
      .catch((err) => {
        setCell(!newOn);
        onShowNotification && onShowNotification(
          roleChangeError(err.status, err.responseData?.detail, 'Permissions not updated. Try again.'),
          'error',
        );
      })
      .finally(() => setPermissionSaving(false));
  };

  if (loading && !me) {
    return <div className="settings-page"><p className="settings-loading">Loading…</p></div>;
  }

  return (
    <div className="settings-page">
      <div className="settings-tabs">
        {settingsTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'assessments' && libraryModel && (
        <ScenariosSection
          model={libraryModel}
          onShowNotification={onShowToast || onShowNotification}
          addOnOpen={addScenarioOnOpen}
          onAddOpened={onRequestHandled}
          returnToAdd={returnToAdd}
          onReturnToAdd={backToAddAssessment}
        />
      )}

      {activeTab === 'organization' && (
        <>
          <OrganizationProfileCard
            me={me}
            features={me?.features || {}}
            onShowNotification={onShowNotification}
            onSaved={() => {
              fetchMe().then(setMe);
              // The app's own copy too: it hands the organisation's zone to the
              // timestamp formatter, which would otherwise keep the old one until a reload.
              onRefreshMe?.();
            }}
            isSuperAdmin={appMe?.is_super_admin || false}
            onOrgSwitch={async (orgId, orgName) => {
              if (orgId) {
                localStorage.setItem('active_org_id', orgId);
                localStorage.setItem('active_org_name', orgName);
              } else {
                localStorage.removeItem('active_org_id');
                localStorage.removeItem('active_org_name');
              }
              const newMe = await fetchMe();
              if (newMe) setMe(newMe);
              if (onRefreshMe) onRefreshMe();
            }}
          />
          {/* Modules section, only visible when the user can edit the org
              (currently only super-admin per the RBAC mapping). Mirrors the
              backend gate on /api/v1/orgs/{id}/modules. Additionally gated by
              `enable_module_framework` so the module-framework UI is hidden
              in environments where the feature isn't ready yet. */}
          {moduleFrameworkEnabled && me?.features?.can_edit_organization && me?.org_id && (
            <ModulesSection orgId={me.org_id} onShowNotification={onShowNotification} />
          )}
        </>
      )}

      {activeTab === 'users' && (
        <div className="settings-section">
          <div className="settings-card settings-users-card">
            <div className="settings-users-card-header">
              <h3 className="settings-section-title settings-users-title">Users</h3>
              <div className="settings-users-header-actions">
                <button type="button" className="settings-add-user-btn" onClick={() => setAddUserOpen(true)}>
                  Add User
                </button>
              </div>
            </div>
            <div className="settings-users-table-wrap">
              <table className="settings-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isEditing = editingUserId === u.id;
                    const menuOpen = openMenuUserId === u.id;
                    const roleName = u.role_name ? (ROLE_DISPLAY_NAMES[u.role_name] || u.role_name) : '—';
                    const nameStr = typeof u.name === 'string' ? u.name : '';
                    const emailStr = typeof u.email === 'string' ? u.email : '';
                    let rawName;
                    if (emailStr && nameStr && nameStr === emailStr) {
                      rawName = emailStr.split('@')[0];
                    } else if (nameStr && nameStr.includes('@')) {
                      rawName = nameStr.split('@')[0];
                    } else if (nameStr) {
                      rawName = nameStr;
                    } else if (emailStr) {
                      rawName = emailStr.split('@')[0];
                    } else {
                      rawName = '—';
                    }
                    const displayName = rawName;
                    const displayEmail = emailStr || nameStr || '—';
                    return (
                      <tr key={u.id}>
                        <td>{displayName}</td>
                        <td>{displayEmail}</td>
                        <td>
                          {isEditing && roles.length > 0 ? (
                            <div className="settings-role-cell">
                              <select
                                className="resui-select resui-select--compact settings-role-select"
                                value={
                                  pendingRoleByUserId[u.id] !== undefined
                                    ? String(pendingRoleByUserId[u.id])
                                    : (u.role_id != null && u.role_id !== '' ? String(u.role_id) : '')
                                }
                                onChange={(e) => {
                                  const newRoleId = e.target.value;
                                  const currentId = u.role_id != null && u.role_id !== '' ? String(u.role_id) : '';
                                  if (newRoleId === currentId) {
                                    setPendingRoleByUserId((prev) => {
                                      const next = { ...prev };
                                      delete next[u.id];
                                      return next;
                                    });
                                  } else {
                                    setPendingRoleByUserId((prev) => ({ ...prev, [u.id]: newRoleId }));
                                  }
                                }}
                              >
                                <option value="">Select role</option>
                                {roles.map((r) => (
                                  <option key={r.id} value={String(r.id)}>
                                    {ROLE_DISPLAY_NAMES[r.role_name] || r.role_name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="resui-btn resui-btn--primary"
                                disabled={savingUserId !== null || pendingRoleByUserId[u.id] === undefined}
                                onClick={async () => {
                                  const roleId = pendingRoleByUserId[u.id];
                                  if (!roleId) return;
                                  setSavingUserId(u.id);
                                  try {
                                    const res = await fetch(`${API_BASE_URL}/api/v1/users/${u.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                                      body: JSON.stringify({ role_id: roleId }),
                                    });
                                    if (res.ok) {
                                      onShowNotification && onShowNotification('Role updated.');
                                      await fetchUsers().then(setUsers);
                                      if (me && String(me.id) === String(u.id) && onLogout) {
                                        onShowNotification && onShowNotification('Your role was changed. Please sign in again.', 'success');
                                        setTimeout(() => onLogout(), 1500);
                                      }
                                    } else {
                                      const data = await res.json().catch(() => ({}));
                                      onShowNotification && onShowNotification(roleChangeError(res.status, data.detail, 'Failed to update role'), 'error');
                                    }
                                  } catch {
                                    onShowNotification && onShowNotification('Failed to update role', 'error');
                                  } finally {
                                    setSavingUserId(null);
                                    setPendingRoleByUserId((prev) => {
                                      const next = { ...prev };
                                      delete next[u.id];
                                      return next;
                                    });
                                    setEditingUserId(null);
                                  }
                                }}
                              >
                                <span className="resui-btn-label">{savingUserId === u.id ? 'Saving…' : 'Save'}</span>
                              </button>
                              <button
                                type="button"
                                className="resui-btn resui-btn--secondary"
                                onClick={() => {
                                  setPendingRoleByUserId((prev) => {
                                    const next = { ...prev };
                                    delete next[u.id];
                                    return next;
                                  });
                                  setEditingUserId(null);
                                }}
                              >
                                <span className="resui-btn-label">Cancel</span>
                              </button>
                            </div>
                          ) : (
                            <span className="settings-role-static">{roleName}</span>
                          )}
                        </td>
                        <td><Chip variant={u.status === 'active' ? 'success' : 'standard'} className="settings-status-badge">{u.status === 'active' ? 'Active' : 'Disabled'}</Chip></td>
                        <td>
                          <div className="settings-actions-wrapper">
                            <button
                              type="button"
                              className="settings-actions-btn"
                              aria-label="Actions"
                              onClick={() => setOpenMenuUserId(menuOpen ? null : u.id)}
                            >
                              ⋯
                            </button>
                            {menuOpen && (
                              <div className="settings-actions-menu">
                                <button
                                  type="button"
                                  className="settings-actions-menu-item"
                                  onClick={() => {
                                    setEditingUserId(u.id);
                                    setOpenMenuUserId(null);
                                  }}
                                >
                                  <Icon name="edit" />Edit
                                </button>
                                <button
                                  type="button"
                                  className="settings-actions-menu-item settings-actions-menu-item--danger"
                                  onClick={() => {
                                    setDeleteConfirmUser(u);
                                    setOpenMenuUserId(null);
                                  }}
                                >
                                  <Icon name="delete" />Delete from Org
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="settings-note">Users listed here can access the application after signing in with their organization account.</p>
          </div>
        </div>
      )}

      {deleteConfirmUser && (
        <div className="settings-confirm-backdrop" onClick={() => setDeleteConfirmUser(null)}>
          <div className="settings-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h4>Remove User</h4>
            <p>Are you sure you want to remove <strong>{deleteConfirmUser.name}</strong> from the organization? This action cannot be undone.</p>
            <div className="settings-confirm-actions">
              <button type="button" className="settings-btn-cancel" onClick={() => setDeleteConfirmUser(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="settings-btn-delete"
                disabled={deletingUserId !== null}
                onClick={async () => {
                  setDeletingUserId(deleteConfirmUser.id);
                  try {
                    const res = await fetch(`${API_BASE_URL}/api/v1/users/${deleteConfirmUser.id}`, {
                      method: 'DELETE',
                      headers: getAuthHeaders(),
                    });
                    if (res.ok || res.status === 204) {
                      onShowNotification && onShowNotification('User removed from organization.');
                      await fetchUsers().then(setUsers);
                    } else {
                      const data = await res.json().catch(() => ({}));
                      onShowNotification && onShowNotification(data.detail || 'Failed to remove user', 'error');
                    }
                  } catch {
                    onShowNotification && onShowNotification('Failed to remove user', 'error');
                  } finally {
                    setDeletingUserId(null);
                    setDeleteConfirmUser(null);
                  }
                }}
              >
                {deletingUserId ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="settings-section">
          <div className="settings-card settings-permissions-card">
            <h3 className="settings-permissions-title">Role permissions</h3>
            <p className="settings-permissions-subtitle">Toggle access for each capability by role.</p>
            <div className="settings-permissions-table-wrap">
              <table className="settings-permissions-table">
                <thead>
                  <tr>
                    <th className="settings-permissions-capability-col">Capability</th>
                    {rolesWithPermissions.map((r) => (
                      <th key={r.id} className="settings-permissions-role-col">{roleDisplayName(r.role_name)}</th>
                    ))}
                  </tr>
                </thead>
                {permissionRows.map((section) => (
                  <tbody key={section.label || 'ungrouped'}>
                    {section.label && (
                      <tr>
                        <th colSpan={rolesWithPermissions.length + 1} scope="rowgroup">{section.label}</th>
                      </tr>
                    )}
                    {section.capabilities.map((cap) => (
                      <tr key={cap.id}>
                        <td className="settings-permissions-capability-cell">{cap.label}</td>
                        {rolesWithPermissions.map((role) => {
                          const on = permissionMatrix[cap.action]?.[role.id] ?? false;
                          return (
                            <td key={role.id} className="settings-permissions-toggle-cell">
                              <label className="resui-switch">
                              <input
                                type="checkbox"
                                className="resui-switch-input"
                                aria-label={`${section.label ? `${section.label}: ` : ''}${cap.label}, ${roleDisplayName(role.role_name)}`}
                                checked={on}
                                disabled={permissionSaving}
                                onChange={() => togglePermission(role, cap, !on)}
                              />
                              <span className="resui-switch-track"><span className="resui-switch-thumb" /></span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </div>
        </div>
      )}

      {addUserOpen && (
        <AddUserModal
          roles={roles}
          onClose={() => setAddUserOpen(false)}
          onSuccess={() => {
            fetchUsers().then(setUsers);
            setAddUserOpen(false);
            if (onShowNotification) onShowNotification('User added. They can sign in with their organization account.');
          }}
          onError={(msg) => onShowNotification && onShowNotification(msg, 'error')}
        />
      )}
    </div>
  );
}

const CLOCK_FORMATS = [
  { value: '12h', label: '12-hour (9:42 PM)' },
  { value: '24h', label: '24-hour (21:42)' },
];

const TIME_DISPLAY_MODES = [
  {
    value: 'everyone',
    label: 'Everyone sees the organization time zone',
    hint: (zone) => `All times show in ${zone || 'UTC'} for every user.`,
  },
  {
    value: 'users',
    label: 'Users can choose their own time zone',
    hint: () => 'Each user sees the time zone set in their profile. Users who have not set one see their device time zone.',
  },
];

const LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB (mirrors the server limit)
const SESSION_TIMEOUT_OPTIONS = ['15 minutes', '30 minutes', '1 hour', '4 hours', 'Never'];

function OrganizationProfileCard({ me, features = {}, onShowNotification, onSaved, isSuperAdmin, onOrgSwitch }) {
  const canEdit = !!features.can_edit_organization;
  // PRD-09: the org logo is gated on the SSO-config capability (System Administrator &
  // Workspace Owner in the RBAC redesign) — enforced server-side too.
  const canManageLogo = !!features.can_configure_sso;
  // PRD-1699: with `role-capabilities` on, SSO setup needs Manage SSO (R7). The save
  // (PATCH /org/profile) needs Edit Organization, and a change to the SSO fields needs
  // Manage SSO as well (resapi, PRD-1704). So a role with Manage SSO alone sees the
  // SSO settings read-only, and one with Edit Organization alone doesn't see them.
  const caps = useCapabilities(features);
  const showSso = !caps.enabled || caps.has('manageSso');
  // PRD-09: Session Timeout + Remember Email are placeholders — gated behind this flag.
  const sessionControlsEnabled = useFlag('org-session-controls');
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(me?.org_display_name || me?.org_name || '');
  // No fallback zone: an organization without one sees an empty, required field rather
  // than a zone it is not using.
  const [timeZone, setTimeZone] = useState(me?.org_primary_time_zone || '');
  const [clockFormat, setClockFormat] = useState(me?.org_clock_format || '12h');
  const [timeDisplayMode, setTimeDisplayMode] = useState(me?.org_time_display_mode || 'everyone');
  const [zones, setZones] = useState([]);
  const [zonesStatus, setZonesStatus] = useState('loading');
  const [dataRegion, setDataRegion] = useState(me?.org_default_data_region || DATA_REGIONS[0] || '');
  const [tenantId, setTenantId] = useState(me?.org_entra_tenant_id || '');
  const [clientId, setClientId] = useState(me?.org_entra_client_id || '');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);

  // PRD-09 org logo. Deferred save: picking/removing in edit mode only previews +
  // marks the form dirty; the actual upload/delete happens on "Save Changes".
  const [logoPreview, setLogoPreview] = useState(null); // object URL or null
  const [pendingLogoFile, setPendingLogoFile] = useState(null); // File staged for save
  const [pendingLogoRemove, setPendingLogoRemove] = useState(false);
  const logoInputRef = useRef(null);
  const logoUrlRef = useRef(null);

  // PRD-09 auth controls — UI-only for now (not yet wired to the backend).
  const [sessionTimeout, setSessionTimeout] = useState(me?.org_session_timeout || '30 minutes');
  const [rememberEmail, setRememberEmail] = useState(me?.org_remember_email_enabled ?? true);
  const [ssoOpen, setSsoOpen] = useState(false);

  const setLogoObjUrl = (objUrl) => {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    logoUrlRef.current = objUrl;
    setLogoPreview(objUrl);
  };

  useEffect(() => {
    let cancelled = false;
    if (me?.org_logo_url) {
      apiService.fetchOrgLogoObjectUrl().then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        setLogoObjUrl(u);
      });
    } else {
      setLogoObjUrl(null);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.org_logo_url]);

  useEffect(
    () => () => {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    },
    []
  );

  const handleLogoPick = () => logoInputRef.current?.click();

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      onShowNotification('Unsupported image type. Use PNG, JPEG, or WebP.', 'error');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      onShowNotification('Image exceeds the 2 MB limit.', 'error');
      return;
    }
    // Preview locally + stage for save; persisted on "Save Changes".
    setLogoObjUrl(URL.createObjectURL(file));
    setPendingLogoFile(file);
    setPendingLogoRemove(false);
  };

  const handleLogoRemove = () => {
    setLogoObjUrl(null);
    setPendingLogoFile(null);
    setPendingLogoRemove(true);
  };

  // The picker says when the list is loading or failed, and offers a retry: an organization
  // with no zone can save nothing until it picks one, so a failed load must not read as an
  // empty list.
  const zonesRequest = useRef(0);
  const loadZones = useCallback(() => {
    const request = ++zonesRequest.current;
    setZonesStatus('loading');
    apiService.getTimeZones()
      .then((list) => {
        if (request !== zonesRequest.current) return;
        setZones(list);
        setZonesStatus('ready');
      })
      .catch(() => { if (request === zonesRequest.current) setZonesStatus('error'); });
  }, []);
  useEffect(() => {
    loadZones();
    return () => { zonesRequest.current += 1; };
  }, [loadZones]);

  // Superadmin org-switching state
  const [allOrgs, setAllOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(localStorage.getItem('active_org_id') || '');
  const [orgSwitching, setOrgSwitching] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    apiService.getAdminOrgs()
      .then((data) => { if (!cancelled) setAllOrgs(Array.isArray(data) ? data : []); })
      .catch((err) => console.error('Failed to fetch orgs:', err));
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  useEffect(() => {
    setDisplayName(me?.org_display_name || me?.org_name || '');
    setTimeZone(me?.org_primary_time_zone || '');
    setClockFormat(me?.org_clock_format || '12h');
    setTimeDisplayMode(me?.org_time_display_mode || 'everyone');
    setDataRegion(me?.org_default_data_region || DATA_REGIONS[0] || '');
    setRememberEmail(me?.org_remember_email_enabled ?? true);
    setTenantId(me?.org_entra_tenant_id || '');
    setClientId(me?.org_entra_client_id || '');
    setClientSecret('');
    // Drop any staged (unsaved) logo change when the profile reloads.
    setPendingLogoFile(null);
    setPendingLogoRemove(false);
  }, [me]);

  const handleCancel = () => {
    // Revert edits + discard any staged logo change, then leave edit mode.
    setDisplayName(me?.org_display_name || me?.org_name || '');
    setTimeZone(me?.org_primary_time_zone || '');
    setClockFormat(me?.org_clock_format || '12h');
    setTimeDisplayMode(me?.org_time_display_mode || 'everyone');
    setDataRegion(me?.org_default_data_region || DATA_REGIONS[0] || '');
    setRememberEmail(me?.org_remember_email_enabled ?? true);
    setTenantId(me?.org_entra_tenant_id || '');
    setClientId(me?.org_entra_client_id || '');
    setClientSecret('');
    setPendingLogoFile(null);
    setPendingLogoRemove(false);
    if (me?.org_logo_url) {
      apiService.fetchOrgLogoObjectUrl().then((u) => setLogoObjUrl(u));
    } else {
      setLogoObjUrl(null);
    }
    setEditing(false);
  };

  const handleSave = async () => {
    if (!canEdit || !timeZone) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/org/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          display_name: displayName.trim() || null,
          primary_time_zone: timeZone || null,
          clock_format: clockFormat,
          time_display_mode: timeDisplayMode,
          default_data_region: dataRegion || null,
          remember_email_enabled: rememberEmail,
          entra_tenant_id: tenantId.trim() || null,
          entra_client_id: clientId.trim() || null,
          ...(clientSecret.trim() ? { entra_client_secret: clientSecret.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onShowNotification(data.detail || 'Failed to save', 'error');
        return;
      }
      // Reflect the Remember Email choice on THIS device immediately (the org
      // value also persists via the PATCH above; other devices sync on next
      // login). On: remember the current user's email now, so login pre-fills.
      // Off: clear the stored email so login stops pre-filling.
      if (rememberEmail) {
        setRememberEmailEnabled(true);
        if (me?.email) setRememberedEmail(me.email);
      } else {
        setRememberEmailEnabled(false);
        clearRememberedEmail();
        clearInflightEmail();
      }
      // Apply the staged org-logo change (if any) as part of Save Changes.
      if (pendingLogoFile) {
        await apiService.uploadOrgLogo(pendingLogoFile);
      } else if (pendingLogoRemove) {
        await apiService.deleteOrgLogo();
      }
      setPendingLogoFile(null);
      setPendingLogoRemove(false);
      setEditing(false);
      onSaved();
      onShowNotification('Organization profile saved');
    } catch (e) {
      onShowNotification(e?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onFieldChange = (setter, value) => {
    setter(value);
  };

  const isEditing = canEdit && editing;
  const displayOptions = (list, current) => [
    ...(current && !list.includes(current) ? [current] : []),
    ...list,
  ];

  return (
    <div className="settings-section">
      {/* Actions bar for the whole Organization tab (Edit / Save / Switch Account). */}
      <div className="settings-org-actions-bar">
        {isSuperAdmin && (
          <button
            type="button"
            className="resui-btn resui-btn--secondary"
            disabled={orgSwitching}
            onClick={async () => {
              setOrgSwitching(true);
              try {
                const selected = allOrgs.find((o) => String(o.id) === selectedOrgId);
                await onOrgSwitch(
                  selectedOrgId || null,
                  selected ? (selected.display_name || selected.orgname) : null
                );
                onShowNotification(
                  selectedOrgId
                    ? `Switched to ${selected?.display_name || selected?.orgname}`
                    : 'Returned to ResolveHD'
                );
              } catch {
                onShowNotification('Failed to switch organization', 'error');
              } finally {
                setOrgSwitching(false);
              }
            }}
          >
            <span className="resui-btn-label">{orgSwitching ? 'Switching…' : 'Switch Account'}</span>
          </button>
        )}
        {canEdit && !editing && (
          <button type="button" className="resui-btn resui-btn--secondary" onClick={() => setEditing(true)}>
            <span className="resui-btn-label">Edit</span>
          </button>
        )}
        {canEdit && editing && (
          <>
            <button type="button" className="settings-btn-cancel-outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="settings-btn-save" onClick={handleSave} disabled={saving || !timeZone}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      </div>

      {/* ── Organization Profile ─────────────────────────────────────────── */}
      <div className="settings-org-card">
        <div className="settings-org-card-header">
          <h3 className="settings-org-card-title">Organization Profile</h3>
        </div>
        <div className="settings-org-card-fields">
          {/* PRD-09: org logo. Click the logo itself to upload/update (gated on the
              SSO-config capability, server-enforced). Others see it read-only. */}
          <div className="settings-org-logo-block">
            {canManageLogo ? (
              <>
                <button
                  type="button"
                  className="settings-org-logo-btn"
                  onClick={handleLogoPick}
                  disabled={!isEditing || saving}
                  title={isEditing ? 'Change logo — PNG, JPEG or WebP, up to 2 MB' : undefined}
                  aria-label="Change organization logo"
                >
                  <span className="settings-org-logo-preview" aria-hidden="true">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Organization logo" />
                    ) : (
                      <Icon name="business" />
                    )}
                  </span>
                  {/* Camera badge + Remove only in edit mode (matches UI_Flow). */}
                  {isEditing && (
                    <span className="settings-org-logo-cam" aria-hidden="true">
                      <Icon name="photo_camera" />
                    </span>
                  )}
                </button>
                {isEditing && logoPreview && (
                  <button
                    type="button"
                    className="settings-org-logo-remove"
                    onClick={handleLogoRemove}
                    disabled={saving}
                  >
                    Remove logo
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="settings-org-logo-preview" aria-hidden="true">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Organization logo" />
                  ) : (
                    <Icon name="business" />
                  )}
                </span>
                <span className="settings-org-logo-hint">
                  Only a System Administrator or Workspace Owner (roles that can manage SSO
                  configuration) can set up the organization logo.
                </span>
              </>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="settings-org-field">
            <label>Organization Name</label>
            {isSuperAdmin ? (
              <select
                className="resui-select settings-org-select"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="">My Organization (ResolveHD)</option>
                {allOrgs.map((org) => (
                  <option key={org.id} value={String(org.id)}>
                    {org.display_name || org.orgname}
                  </option>
                ))}
              </select>
            ) : (
              <select
                className="resui-select settings-org-select"
                value={displayName}
                onChange={(e) => onFieldChange(setDisplayName, e.target.value)}
                disabled={!isEditing}
              >
                <option value="">Select organization...</option>
                {displayOptions(ORGANIZATION_NAMES, displayName).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
          <div className={`settings-org-field${isEditing && !timeZone ? ' resui-field--error' : ''}`}>
            <label htmlFor="settings-org-time-zone">
              Primary Time Zone <span className="resui-field-required">*</span>
            </label>
            <TimeZonePicker
              id="settings-org-time-zone"
              value={timeZone}
              onChange={(zone) => onFieldChange(setTimeZone, zone)}
              zones={zones}
              status={zonesStatus}
              onRetry={loadZones}
              disabled={!isEditing}
              required
              invalid={isEditing && !timeZone}
              describedBy="settings-org-time-zone-help"
            />
            <span id="settings-org-time-zone-help" className="resui-field-helper">
              {!timeZone
                ? `No time zone set, so times and report periods use UTC.${isEditing ? ' Choose one to save.' : ''}`
                : 'Report periods such as Today and Last 90 days always use this zone, so everyone sees the same totals.'}
            </span>
          </div>
          <fieldset className="settings-org-field settings-org-fieldset">
            <legend>Time Display</legend>
            <div className="settings-org-radios settings-org-radios--stacked">
              {TIME_DISPLAY_MODES.map((m) => (
                <div key={m.value} className="settings-org-radio-option">
                  <label className="resui-radio">
                    <input
                      type="radio"
                      className="resui-radio-input"
                      name="settings-org-time-display"
                      value={m.value}
                      checked={timeDisplayMode === m.value}
                      onChange={() => onFieldChange(setTimeDisplayMode, m.value)}
                      disabled={!isEditing}
                      aria-describedby={`settings-org-time-display-${m.value}-hint`}
                    />
                    <span className="resui-radio-mark" />
                    <span className="resui-radio-label">{m.label}</span>
                  </label>
                  <span id={`settings-org-time-display-${m.value}-hint`} className="resui-field-helper settings-org-radio-hint">
                    {m.hint(timeZone)}
                  </span>
                </div>
              ))}
            </div>
          </fieldset>
          <fieldset className="settings-org-field settings-org-fieldset">
            <legend>Clock Format</legend>
            <div className="settings-org-radios">
              {CLOCK_FORMATS.map((c) => (
                <label key={c.value} className="resui-radio">
                  <input
                    type="radio"
                    className="resui-radio-input"
                    name="settings-org-clock-format"
                    value={c.value}
                    checked={clockFormat === c.value}
                    onChange={() => onFieldChange(setClockFormat, c.value)}
                    disabled={!isEditing}
                  />
                  <span className="resui-radio-mark" />
                  <span className="resui-radio-label">{c.label}</span>
                </label>
              ))}
            </div>
            <span className="resui-field-helper">Applies to every user in the organization.</span>
          </fieldset>
          <div className="settings-org-field">
            <label>Default Data Region</label>
            <select
              className="resui-select settings-org-select"
              value={dataRegion}
              onChange={(e) => onFieldChange(setDataRegion, e.target.value)}
              disabled={!isEditing}
            >
              {displayOptions(DATA_REGIONS, dataRegion).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Authentication ───────────────────────────────────────────────── */}
      <div className="settings-org-card settings-org-card--sections">
        <div className="settings-org-card-header">
          <h3 className="settings-org-card-title">Authentication</h3>
        </div>
        <div className="settings-org-card-fields">
          {/* SSO — collapsible (Tenant / Client / Secret). */}
          {showSso && (
          <div className="settings-org-collapse">
            <button
              type="button"
              className="settings-org-collapse-head"
              aria-expanded={ssoOpen}
              onClick={() => setSsoOpen((o) => !o)}
            >
              <span className="settings-org-row-text">
                <span className="settings-org-row-title">Single Sign-On (SSO)</span>
                <span className="settings-org-row-desc">Microsoft Entra ID</span>
              </span>
              <Icon name={ssoOpen ? 'expand_less' : 'expand_more'} className="settings-org-collapse-chev" />
            </button>
            {ssoOpen && (
              <div className="settings-org-collapse-body">
                <div className="settings-org-field">
                  <label htmlFor="org-tenant-id">Tenant ID</label>
                  <SecretInput
                    id="org-tenant-id"
                    value={tenantId}
                    onChange={(e) => onFieldChange(setTenantId, e.target.value)}
                    disabled={!isEditing}
                    placeholder="Microsoft Entra tenant ID (UUID)"
                  />
                </div>
                <div className="settings-org-field">
                  <label htmlFor="org-client-id">Client ID</label>
                  <SecretInput
                    id="org-client-id"
                    value={clientId}
                    onChange={(e) => onFieldChange(setClientId, e.target.value)}
                    disabled={!isEditing}
                    placeholder="Microsoft Entra application (client) ID"
                  />
                </div>
                <div className="settings-org-field">
                  <label htmlFor="org-client-secret">Client Secret</label>
                  <SecretInput
                    id="org-client-secret"
                    value={clientSecret}
                    onChange={(e) => onFieldChange(setClientSecret, e.target.value)}
                    disabled={!isEditing}
                    placeholder="Leave blank to keep existing"
                  />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Session Timeout + Remember Email — placeholders, gated behind org-session-controls. */}
          {sessionControlsEnabled && (
            <>
              <div className="settings-org-row">
                <div className="settings-org-row-text">
                  <div className="settings-org-row-title">Session Timeout</div>
                  <div className="settings-org-row-desc">Sign users out after a period of inactivity.</div>
                </div>
                <select
                  className="resui-select settings-org-select settings-org-row-control"
                  value={sessionTimeout}
                  onChange={(e) => setSessionTimeout(e.target.value)}
                  disabled={!isEditing}
                >
                  {SESSION_TIMEOUT_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="settings-org-row">
                <div className="settings-org-row-text">
                  <div className="settings-org-row-title">Remember Email</div>
                  <div className="settings-org-row-desc">
                    Pre-fill a returning user's email at sign-in. Turn off for shared workstations.
                  </div>
                </div>
                <label className={`resui-switch${!isEditing ? ' resui-switch--readonly' : ''}`}>
                  <input
                    type="checkbox"
                    className="resui-switch-input"
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                    disabled={!isEditing}
                  />
                  <span className="resui-switch-track"><span className="resui-switch-thumb" /></span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}

const USER_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

function AddUserModal({ roles, onClose, onSuccess, onError }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState('active');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (roles.length && !roleId) setRoleId(String(roles[0].id));
  }, [roles, roleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      onError('Please enter a valid email address.');
      return;
    }
    if (!roleId) {
      onError('Please select a role.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          email: trimmedEmail,
          name: name.trim() || null,
          role_id: roleId,
          status: status,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onError(Array.isArray(data.detail) ? data.detail.join(' ') : roleChangeError(res.status, data.detail, 'Failed to add user'));
        return;
      }
      onSuccess();
    } catch (_) {
      onError('Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-modal-backdrop" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h3>Add user</h3>
          <button type="button" className="settings-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-form-row">
            <label>Name</label>
            <input className="resui-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>
          <div className="settings-form-row">
            <label>Email</label>
            <input className="resui-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" required />
          </div>
          <div className="settings-form-row">
            <label>Role</label>
            <select value={roleId} onChange={e => setRoleId(e.target.value)} required className="resui-select">
              <option value="">Select role</option>
              {roles.map((r) => (
                <option key={r.id} value={String(r.id)}>{ROLE_DISPLAY_NAMES[r.role_name] || r.role_name}</option>
              ))}
            </select>
          </div>
          <div className="settings-form-row">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="resui-select">
              {USER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="settings-modal-actions">
            <button type="button" className="settings-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="settings-btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Settings;
